/**
 * Route-scoped SEO metadata for the SPA. Wraps react-helmet-async so any page
 * can drop <PageMeta path="/foo" /> at the top of its component to get:
 *   - Unique <title>
 *   - Unique <meta name="description">
 *   - <link rel="canonical"> (self-referencing, strips query strings — so
 *     /signup?pkg=starter collapses to /signup)
 *   - <meta name="robots" content="noindex,nofollow"> for app-internal routes
 *
 * NOTE: React renders these client-side. Modern Googlebot / Bingbot fully
 * execute JS and pick them up, but plain-HTML curl-based audits will see the
 * <title>Doctor Digital Identity Platform</title> from index.html. For SSR
 * of marketing pages we'd need a build-time prerender step; this is fine for
 * now because published /dr/<slug> pages are SSR'd by the backend and the
 * marketing pages have static defaults in index.html that are already good.
 */
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";

const PUBLIC_BASE = (
    process.env.REACT_APP_PUBLIC_BASE_URL ||
    (typeof window !== "undefined" ? window.location.origin : "") ||
    "https://doctorsprofile.in"
).replace(/\/$/, "");

// CMS-editable marketing SEO override — fetched once per SPA mount.
let _cmsSeoCache = null;
async function _loadCmsSeo() {
    if (_cmsSeoCache) return _cmsSeoCache;
    try {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const r = await fetch(`${base}/api/public/site-content`);
        const data = await r.json();
        _cmsSeoCache = (data && data.marketing_seo) || {};
    } catch { _cmsSeoCache = {}; }
    return _cmsSeoCache;
}

// Map SPA path → CMS key (only marketing routes are user-overridable).
const _CMS_ROUTE_KEY = { "/": "home", "/pricing": "pricing", "/about": "about", "/contact": "contact" };

// Fully indexable, unique per-page meta for marketing pages.
const MARKETING = {
    "/": {
        title: "Doctor Digital Identity — AI-Refined Website & QR Card for Indian Doctors",
        description:
            "Build a verified doctor digital identity: AI-refined biography, SEO website, QR visiting card, published under /dr/your-name. Trusted by clinicians across India.",
    },
    "/pricing": {
        title: "Doctor Digital Identity Pricing — Plans from ₹5,000 (One-Time + Optional AMC)",
        description:
            "5 packages from Starter (₹5,000, 20 features) to Legacy Platinum (₹20,000, 121 features). One-time onboarding + optional annual AMC. GST included.",
    },
    "/terms": {
        title: "Terms of Service — Doctor Digital Identity Platform",
        description:
            "Terms governing use of the Doctor Digital Identity Platform: doctor onboarding, AI-refined content approval, publication, payments, refunds, and data usage.",
    },
    "/privacy": {
        title: "Privacy Policy — Doctor Digital Identity Platform",
        description:
            "How we collect, store, and process doctor and patient data on the Doctor Digital Identity Platform — GDPR-inspired, HUMANITY FIRST policy.",
    },
};

// App-internal routes — never indexable. Titles are still per-route so tab
// text is useful even though search engines are told to ignore them.
const APP_INTERNAL = {
    "/login": { title: "Log in — Doctor Digital Identity" },
    "/signup": { title: "Create your Doctor Digital Identity" },
    "/forgot-password": { title: "Reset password — Doctor Digital Identity" },
    "/reset-password": { title: "Reset password — Doctor Digital Identity" },
    "/verify-otp": { title: "Verify OTP — Doctor Digital Identity" },
    "/verify-email": { title: "Verify email — Doctor Digital Identity" },
    "/verify-mobile": { title: "Verify mobile — Doctor Digital Identity" },
    "/admin/login": { title: "Admin — Doctor Digital Identity" },
    "/super-admin/login": { title: "Super Admin — Doctor Digital Identity" },
};

// Prefixes considered app-internal (any deeper path noindex'd).
const NOINDEX_PREFIXES = [
    "/login",
    "/signup",
    "/forgot-password",
    "/reset-password",
    "/verify-",
    "/admin",
    "/super-admin",
    "/dashboard",
    "/doctor",
];

function isNoindexPath(path) {
    return NOINDEX_PREFIXES.some((p) => path === p || path.startsWith(p + "/") || path.startsWith(p));
}

function metaFor(path) {
    // Normalise: drop trailing slash (except root), strip query string.
    let p = path || "/";
    p = p.split("?")[0].split("#")[0];
    if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);

    if (MARKETING[p]) return { ...MARKETING[p], noindex: false, canonicalPath: p };
    if (APP_INTERNAL[p]) return { ...APP_INTERNAL[p], noindex: true, canonicalPath: p };
    if (isNoindexPath(p)) {
        return {
            title: "Doctor Digital Identity Platform",
            description: undefined,
            noindex: true,
            canonicalPath: p,
        };
    }
    // Fallback: default marketing meta.
    return {
        ...MARKETING["/"],
        noindex: false,
        canonicalPath: p,
    };
}

export default function PageMeta({ path, title, description, noindex }) {
    // If a path isn't passed, fall back to window.location.pathname at render.
    const resolved =
        path ||
        (typeof window !== "undefined" ? window.location.pathname : "/");
    const base = metaFor(resolved);
    const [cmsOverride, setCmsOverride] = useState(null);
    useEffect(() => {
        const key = _CMS_ROUTE_KEY[resolved];
        if (!key) return;
        _loadCmsSeo().then((seo) => {
            if (seo && seo[key] && (seo[key].title || seo[key].description)) setCmsOverride(seo[key]);
        });
    }, [resolved]);
    const finalTitle = title || (cmsOverride?.title) || base.title;
    const finalDesc = description !== undefined ? description : ((cmsOverride?.description) || base.description);
    const finalNoindex = noindex !== undefined ? noindex : base.noindex;
    const canonical = `${PUBLIC_BASE}${base.canonicalPath}`;

    return (
        <Helmet prioritizeSeoTags>
            <title>{finalTitle}</title>
            {finalDesc && <meta name="description" content={finalDesc} />}
            <link rel="canonical" href={canonical} />
            {finalNoindex ? (
                <meta name="robots" content="noindex,nofollow" />
            ) : (
                <meta name="robots" content="index,follow" />
            )}
            {/* Open Graph — same values reused for socials */}
            <meta property="og:title" content={finalTitle} />
            {finalDesc && <meta property="og:description" content={finalDesc} />}
            <meta property="og:url" content={canonical} />
            <meta property="og:type" content="website" />
            <meta name="twitter:card" content="summary_large_image" />
        </Helmet>
    );
}
